import "./AgentAvailabilityPopUs.css";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DateRange, Range, RangeKeyDict } from "react-date-range";
import { RootState } from "../../../../../../../../../../../../../redux/store";
import { useUpdateAgentBreaksMutation } from "../../../../../../../../../../../../../redux/services/userManagementApi";
import { endOfDay, startOfDay } from "date-fns";
import { customToast } from "../../../../../../../../../../../../../utils/custom-toast/custom-toast";
import { AgentAvailabilityPopUpProps } from "../../../../utils/types";
import Button from "../../../../../../../../../../../../../components/Button/Button";
import ChevronLeftIcon from "../../../../../../../../../../../../../assets/svg/chevron-left-icon";

export const AgentAvailabilityPopUp = ({
  open,
  onClose,
  initialAgentBreaksData,
}: AgentAvailabilityPopUpProps) => {
  const { userIdForEditOrView } = useSelector(
    (state: RootState) => state?.userManagement
  );

  const [updateAgentBreaks, { isLoading: isUpdating }] =
    useUpdateAgentBreaksMutation();

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [localAgentBreaksData, setLocalAgentBreaksData] = useState(
    initialAgentBreaksData
  );
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const dateRangeRef = useRef<Range | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const agentBreaksData = localAgentBreaksData;

  const disabledDates = useMemo(() => {
    const dates = new Set<string>();

    const getLocalDateString = (utcDateStr: string) => {
      const date = new Date(utcDateStr);

      const userTimezoneOffset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date.getTime() - userTimezoneOffset);

      return localDate.toISOString().split("T")[0];
    };

    agentBreaksData?.upcoming_breaks?.forEach(
      (breakItem: { start_date: string; end_date: string }) => {
        const startLocalStr = getLocalDateString(breakItem?.start_date);
        const endLocalStr = getLocalDateString(breakItem?.end_date);

        const start = new Date(startLocalStr);
        const end = new Date(endLocalStr);

        const current = new Date(start);
        while (current <= end) {
          dates?.add(current?.toISOString()?.split("T")[0]);
          current?.setDate(current?.getDate() + 1);
        }
      }
    );

    return dates;
  }, [agentBreaksData]);

  const handleReset = useCallback(() => {
    setDateRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
    dateRangeRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    handleReset();
  }, [onClose, handleReset]);

  const handleSelect = useCallback((ranges: RangeKeyDict) => {
    dateRangeRef.current = ranges.selection;
    setDateRange([ranges.selection]);
  }, []);

  const handleSave = useCallback(async () => {
    if (!dateRangeRef.current) {
      return;
    }

    if (
      !dateRange[0] ||
      !userIdForEditOrView ||
      !dateRange[0].startDate ||
      !dateRange[0].endDate
    ) {
      return;
    }

    const { startDate, endDate } = dateRange[0];

    const localStartDate = startOfDay(startDate);
    const localEndDate = endOfDay(endDate);

    const startUtc = localStartDate.toISOString();
    const endUtc = localEndDate.toISOString();

    try {
      await updateAgentBreaks({
        userId: userIdForEditOrView,
        start_date: startUtc,
        end_date: endUtc,
      }).unwrap();

      customToast("Leave added successfully", "success");
    } catch (error: unknown) {
      const errorDetail =
        error && typeof error === "object" && "data" in error
          ? (error.data as { detail?: string })?.detail
          : undefined;
      customToast(errorDetail || "Failed to add break", "error");
    } finally {
      handleClose();
    }
  }, [dateRange, userIdForEditOrView, updateAgentBreaks, handleClose]);

  const renderNavigator = (
    currFocusedDate: Date,
    changeShownDate: (value: string | number | Date) => void
  ) => {
    const monthYear = currFocusedDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    const today = new Date();
    const isCurrentMonth =
      currFocusedDate.getMonth() === today.getMonth() &&
      currFocusedDate.getFullYear() === today.getFullYear();

    return (
      <div className="flex items-center justify-between w-full px-2">
        {!isCurrentMonth && (
          <button
            onClick={() =>
              changeShownDate(
                new Date(
                  currFocusedDate.setMonth(currFocusedDate.getMonth() - 1)
                )
              )
            }
          >
            <ChevronLeftIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk font-normal! stroke-2!" />
          </button>
        )}
        {isCurrentMonth && <div className="w-5" />}
        <span className="text-base font-semibold text-heading-text dark:text-heading-text-dk">
          {monthYear}
        </span>
        <button
          onClick={() =>
            changeShownDate(
              new Date(currFocusedDate.setMonth(currFocusedDate.getMonth() + 1))
            )
          }
        >
          <ChevronLeftIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk rotate-180 font-normal! stroke-2!" />
        </button>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        open &&
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      dateRangeRef.current = null;
    };
  }, [open, handleClose]);

  useEffect(() => {
    if (initialAgentBreaksData) {
      setLocalAgentBreaksData(initialAgentBreaksData);
    }
  }, [initialAgentBreaksData]);

  useEffect(() => {
    if (open) {
      const trigger = document.activeElement?.closest("button") as HTMLElement;
      if (trigger) {
        triggerRef.current = trigger;
        const rect = trigger.getBoundingClientRect();
        setPosition({
          top: rect.top - 380,
          left: rect.left,
        });
      }
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-9999999999 w-[320px] bg-pure-white dark:bg-pure-white-dk rounded-lg shadow-lg"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <DateRange
        ranges={dateRange}
        onChange={handleSelect}
        months={1}
        direction="horizontal"
        showDateDisplay={false}
        showMonthAndYearPickers={false}
        rangeColors={["#2563eb"]}
        color="#2563eb"
        className="availability-leaves-popup availability-popup-shadow"
        minDate={new Date()}
        weekdayDisplayFormat="EEEEE"
        monthDisplayFormat="MMMM yyyy"
        navigatorRenderer={renderNavigator}
        disabledDates={Array?.from(disabledDates)?.map(
          (date) => new Date(date)
        )}
      />
      <div className="p-4 border-t border-gray-100 dark:border-gray-100-dk bg-pure-white dark:bg-pure-white-dk rounded-b-lg">
        <div className="flex gap-2">
          <Button
            innerText="Reset"
            onClick={handleReset}
            buttonContainerClass="flex-1 bg-transparent border border-border-gray dark:border-border-gray-dk text-verba-blue dark:text-verba-blue-dk text-sm font-normal py-2 px-5 rounded-sm outline-none hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
          />
          <Button
            innerText="Save"
            disabled={
              !dateRangeRef.current ||
              !dateRange[0]?.startDate ||
              !dateRange[0]?.endDate ||
              disabledDates?.has(
                dateRange[0]?.endDate?.toLocaleDateString("en-CA")
              )
            }
            isLoading={isUpdating}
            loaderColor="white"
            onClick={handleSave}
            buttonContainerClass="flex-1 bg-verba-blue dark:bg-verba-blue-dk text-white text-sm font-normal py-2 px-5 rounded-sm outline-none disabled:!opacity-50 disabled:!cursor-not-allowed"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};
